import React, { useEffect, useRef, useState } from 'react';
import { Card, Button, Divider, Typography, Space, Popconfirm } from 'antd';
import { CheckOutlined } from '@ant-design/icons';
import { BaseSelect } from '@app/components/common/selects/BaseSelect/BaseSelect';
import { BaseRow } from '@app/components/common/BaseRow/BaseRow';
import { BaseCol } from '@app/components/common/BaseCol/BaseCol';
import TestDetailsModal from './TestDetailsModal';
import ResultsModal from './LabResultsModal';
import {
  editOrviewLabTests,
  generateLabReport,
  getLabServiceCategories,
  getLabServices,
  RemoveLabTest,
  uploadLabCertificate,
} from '@app/api/labs.api';
import { notificationController } from '@app/controllers/notificationController';
import { EditViewLabResultResponse, EditViewLabTestResponse, ResultModalData } from '@app/models/labs/LabRequest';
import { getTestIcon, statusColors } from '@app/utils/labItemUtilils';
import { notification } from 'antd';
import moment from 'moment';
import useApiErrorHandler from '@app/components/Error/ErrorMsgHandling';

const { Text } = Typography;

interface DivisionalItemsCardProps {
  divisionId: string;
  divisionName: string;
  divisionCode?: string;
  status: string | null;
  lab_id: number | null;
  deptId?: number | null;
  context: string | null;
  alreadyAddedTests: any[];
  onTestsUpdated?: (updatedTests: any[]) => void;
  status_step: string;
  lab_type: string;
}

const DivisionalItemsCard: React.FC<DivisionalItemsCardProps> = ({
  divisionId,
  divisionCode,
  divisionName,
  status,
  lab_id,
  deptId,
  context,
  alreadyAddedTests,
  onTestsUpdated,
  status_step,
  lab_type,
}) => {
  const [selectedTestValue, setSelectedTestValue] = useState<string | null>(null);
  const [selectedTestLabel, setSelectedTestLabel] = useState<string | null>(null);
  const [resultsModalVisible, setResultsModalVisible] = useState(false);
  const [selectedResultsTestName, setSelectedResultsTestName] = useState<string | null>(null);
  const [modalVisible, setModalVisible] = useState(false);

  const [editMode, setEditMode] = useState(false);
  const [loadingEdit, setLoadingEdit] = useState(false);
  const [loadingEditIndex, setLoadingEditIndex] = useState<number | null>(null);
  const [AssigningData, setAssigningData] = useState<{
    assigning_parameters: any[];
    lab_services: any[];
    technician_options: any[];
    selected_lab_services?: any[];
    additionalInfo?: {
      description?: string;
      technicianId?: number | null;
    };
    wf_actions?: string[];
    service_cat_id?: number;
    lab_samples?: any[];
  } | null>(null);
  const [resultsData, setResultsData] = useState<ResultModalData | null>(null);
  const [resultModalMode, setResultModalMode] = useState<'add' | 'edit' | 'view'>('view');
  const [testOptions, setTestOptions] = useState<{ label: string; value: number }[]>([]);
  const [testModalMode, setTestModalMode] = useState<string | null>(null);
  const [currentTests, setCurrentTests] = useState(alreadyAddedTests);
  const fileInputRefs = useRef<Array<HTMLInputElement | null>>([]);
  const [wf_actions, setWFActions] = useState<string[]>([]);
  const [uploadedFiles, setUploadedFiles] = useState<{
    [key: string]: {
      uploadedAt: any;
      name: string;
      url: string;
    };
  }>({});
  const [dropdownTestValue, setDropdownTestValue] = useState<string | null>(null);
  const [reportUrls, setReportUrls] = useState<Record<number, string>>({});

  const handleErrorMessages = useApiErrorHandler();

  useEffect(() => {
    setCurrentTests(alreadyAddedTests);
  }, [alreadyAddedTests]);

  const getStepStatus = (status: any, status_step: string) => {
    if (!status) return 'PENDING';

    if (status_step === 'step-1' && status?.step_one) return status.step_one;
    if (status_step === 'step-2' && status?.step_two) return status.step_two;
    if (status_step === 'step-3' && status?.step_three) return status.step_three;

    return 'UNKNOWN';
  };

  console.log('current: ', currentTests);

  const testItems = currentTests.map((test, index) => ({
    service_cat_id: test.lab_request_service_category_id,
    maintest_id: test.id.toString(),
    code: test.code,
    name: test.name,
    user_id: test.user_id,
    test_status: lab_type === 'DIRECT_SALES' ? test.status : getStepStatus(test.status, status_step),
    icon: getTestIcon(test.type),
    status_label: lab_type === 'DIRECT_SALES' ? test.status : null,
    status_label_ds: test.status_label_ds || test.status || 'Pending',
    // Use reportUrls state for generated reports
    generated_report_url: reportUrls[index] || test?.reportData?.generated_report_url,
    generated_report: test?.reportData?.generated_report,
    // Use uploadedFiles state for certified reports
    certified_report: uploadedFiles[test.id.toString()]?.name || test?.reportData?.certified_report,
    certified_report_url: uploadedFiles[test.id.toString()]?.url || test?.reportData?.certified_report_url,
    hasGeneratedReport: !!(reportUrls[index] || test?.reportData?.generated_report_url),
    hasCertifiedReport: !!(uploadedFiles[test.id.toString()]?.url || test?.reportData?.certified_report_url),
    result_report_url: test?.reportData?.result_report_url,
  }));

  const handleTestUpdated = (updatedTests: any[]) => {
    console.log('Updated tests received from modal:', updatedTests);

    if (!updatedTests || updatedTests.length === 0) {
      console.log('No updated tests received, skipping update');
      return;
    }

    setCurrentTests((prevTests) => {
      const updatedTestsArray = [...prevTests];

      updatedTests.forEach((updatedTest) => {
        const existingTestIndex = updatedTestsArray.findIndex((existingTest) => {
          // Primary match: Use lab_request_service_category_id as the unique identifier
          // This should be unique for each test instance, even if they share the same main test ID
          if (
            updatedTest.lab_request_service_category_id &&
            existingTest.lab_request_service_category_id === updatedTest.lab_request_service_category_id
          ) {
            return true;
          }

          if (updatedTest.id && existingTest.id && !updatedTest.lab_request_service_category_id) {
            const mainTestId = updatedTest.id.toString();
            const existingTestsWithSameMainId = updatedTestsArray.filter(
              (test) => test.id && test.id.toString() === mainTestId,
            );

            // Only match if there's exactly one existing test with this main ID
            if (existingTestsWithSameMainId.length === 1 && existingTest.id.toString() === mainTestId) {
              return true;
            }
          }

          return false;
        });

        if (existingTestIndex !== -1) {
          // Update existing test
          const existingTest = updatedTestsArray[existingTestIndex];

          const mergedTest = {
            ...existingTest,

            ...(updatedTest.lab_request_service_category_id && {
              lab_request_service_category_id: updatedTest.lab_request_service_category_id,
            }),
            ...(updatedTest.id && { id: updatedTest.id }),
            ...(updatedTest.code && { code: updatedTest.code }),
            ...(updatedTest.name && { name: updatedTest.name }),
            ...(updatedTest.user_id !== undefined && { user_id: updatedTest.user_id }),
            ...(updatedTest.type && { type: updatedTest.type }),

            // Add status_label_ds handling here
            ...(updatedTest.status_label_ds !== undefined && {
              status_label_ds: updatedTest.status_label_ds,
            }),

            status:
              typeof updatedTest.status === 'string' && lab_type !== 'DIRECT_SALES'
                ? { [status_step]: updatedTest.status }
                : updatedTest.status !== undefined
                ? updatedTest.status
                : existingTest.status,
          };

          updatedTestsArray[existingTestIndex] = mergedTest;
          console.log('Updated existing test:', mergedTest);
        } else {
          // Add new test
          const newTest = {
            lab_request_service_category_id: updatedTest.lab_request_service_category_id,
            id: updatedTest.id,
            code: updatedTest.code,
            name: updatedTest.name,
            user_id: updatedTest.user_id,
            status: updatedTest.status || null,
            status_label_ds: updatedTest.status_label_ds || updatedTest.status || 'Pending',
            type: updatedTest.type || 'default',
          };

          updatedTestsArray.push(newTest);
          console.log('Added new test:', newTest);
        }
      });

      console.log('Final updated tests array:', updatedTestsArray);
      return updatedTestsArray;
    });

    setSelectedTestValue(null);
    setSelectedTestLabel(null);
    setDropdownTestValue(null);
  };
  console.log('updated current tests: ', currentTests);

  const getAddTestButtonText = () => {
    switch (lab_type) {
      case 'VACCINE_MANUFACTURING':
        return 'Add Vaccine';
      case 'DIRECT_SALES':
        return 'Add Product';
      default:
        return 'Add Test';
    }
  };

  const handleAddTest = async () => {
    if (!selectedTestValue || !selectedTestLabel) return;
    setSelectedTestLabel(selectedTestLabel);
    setLoadingEdit(true);
    setTestModalMode('add');
    try {
      const res = await getLabServices(Number(lab_id), Number(selectedTestValue));
      setAssigningData({
        assigning_parameters: res.lab_service_category.lab_assigning_parameters,
        lab_services: res.lab_service_category.lab_services,
        technician_options: res.technician_options || [],
      });
      setModalVisible(true);
    } catch (err) {
      handleErrorMessages(err);
      console.error('Failed to fetch lab services:', err);
    } finally {
      setLoadingEdit(false);
    }
  };

  const handleRemoveTest = async (testItem: any, index: number) => {
    setLoadingEdit(true);
    try {
      const response = await RemoveLabTest(Number(lab_id), Number(deptId), testItem.service_cat_id);
      if (response) {
        notificationController.success({ message: response.message || 'Removed successfully' });

        setCurrentTests((prevTests) => prevTests.filter((_, i) => i !== index));
      }
    } catch (err: any) {
      // notificationController.error({ message: error.message || 'Failed to remove test' });
      handleErrorMessages(err);
    } finally {
      setLoadingEdit(false);
    }
  };

  const handleTestAction = async (testItem: any, index: number, mode: 'edit' | 'view') => {
    setLoadingEditIndex(index);
    try {
      const res = await editOrviewLabTests(Number(lab_id), Number(deptId), testItem.service_cat_id);
      const data = res as EditViewLabTestResponse;

      const structuredData = {
        service_cat_id: data.lab_request_service_category.id,
        assigning_parameters: data.lab_request_service_category.assigning_parameters,
        lab_services: data.lab_request_service_category.lab_service_category.lab_services,
        selected_lab_services: data.lab_request_service_category.lab_request_services,
        technician_options: data.lab_request_service_category?.technician_options || [],
        additionalInfo: {
          remarks: data.lab_request_service_category?.description || '',
          technicianId: data.lab_request_service_category?.user_id || null,
        },
        wf_actions: data.lab_request_service_category.wf_actions_can_do || [],
        lab_samples: data.lab_request_service_category.lab_request_sample || [],
      };

      setAssigningData(structuredData);
      const testName = testItem.code + ' - ' + testItem.name;
      setSelectedTestLabel(testName);
      setSelectedTestValue(testItem.maintest_id);

      if (mode === 'edit') {
        setDropdownTestValue(testItem.maintest_id);
      } else {
        setDropdownTestValue(null);
      }

      setTestModalMode(mode);
      setModalVisible(true);
    } catch (err) {
      handleErrorMessages(err);
      console.error('Failed to fetch lab services:', err);
    } finally {
      setLoadingEditIndex(null);
    }
  };

  const handleResultParameters = async (testItem: any, index: number, mode: 'add' | 'edit' | 'view') => {
    setLoadingEditIndex(index);
    try {
      const res = await editOrviewLabTests(Number(lab_id), Number(deptId), testItem.service_cat_id);
      const data = res as EditViewLabResultResponse;
      const resultData: ResultModalData = {
        lab_request_service_category_id: data.lab_request_service_category.id,
        result_format: data.lab_request_service_category.lab_service_category.test_result_format,
        has_test_result_attachment: data.lab_request_service_category.has_test_result_attachment,
        result_report: data.lab_request_service_category.lab_request_report?.result_report || undefined,
        result_report_url: data.lab_request_service_category.lab_request_report?.result_report_url || undefined,
        lab_request_department_id: data.lab_request_service_category.lab_request_department_id,
        lab_service_category_id: data.lab_request_service_category.lab_service_category_id,
        lab_request_services: data.lab_request_service_category.lab_request_services,
        wf_actions: data.lab_request_service_category.wf_actions_can_do || [],
        remarks: data.lab_request_service_category.remarks,
        results_remarks: data.lab_request_service_category.results_remarks,
      };

      const testName = testItem.code + ' - ' + testItem.name;
      setWFActions(resultData.wf_actions || []);
      setSelectedResultsTestName(testName);
      setSelectedTestValue(testItem.maintest_id);
      setResultsData(resultData);
      setResultModalMode(mode);
      setResultsModalVisible(true);
    } catch (err) {
      console.error('Failed to fetch lab services:', err);
      // notificationController.error({ message: String(err) });
      handleErrorMessages(err);
    } finally {
      setLoadingEditIndex(null);
    }
  };

  const handleUpload = async (testItem: any, index: number, event?: React.ChangeEvent<HTMLInputElement>) => {
    setLoadingEditIndex(index);
    try {
      const file = event?.target?.files?.[0];
      if (!file) return;

      if (!lab_id || !deptId) {
        notificationController.error({ message: 'Lab ID or Department ID is missing' });
        return;
      }

      const formData = new FormData();
      formData.append('file', file, file.name);
      formData.append('filename', file.name);
      formData.append('test_id', testItem.maintest_id);

      const response = await uploadLabCertificate(lab_id, deptId, testItem.service_cat_id, formData);

      console.log('Upload response:', response);

      if (response.data?.lab_request_report) {
        const reportData = response.data.lab_request_report;
        const certifiedReport = reportData.certified_report;
        const certifiedReportUrl = reportData.certified_report_url;

        setUploadedFiles((prev) => ({
          ...prev,
          [testItem.maintest_id]: {
            name: certifiedReport,
            url: certifiedReportUrl,
            uploadedAt: new Date().toISOString(),
          },
        }));

        notificationController.success({ message: response.message || 'File uploaded successfully!' });
      } else {
        notificationController.error({ message: 'Upload response is missing report data' });
      }
    } catch (err) {
      //handleErrorMessages(err);
      console.error('Upload failed:', err);
      notificationController.error({ message: String(err) });
    } finally {
      setLoadingEditIndex(null);
      if (event?.target) {
        event.target.value = '';
      }
    }
  };

  const handleEditClick = async () => {
    setLoadingEdit(true);
    try {
      const res = await getLabServiceCategories(Number(lab_id), Number(divisionId));
      const options = res.lab_service_category_options.map((item: any) => ({
        value: item.value,
        label: item.label,
      }));
      setTestOptions(options);
      setEditMode(true);
    } catch (err) {
      handleErrorMessages(err);
      // console.error('Failed to fetch service categories', error);
    } finally {
      setLoadingEdit(false);
    }
  };

  const handleDoneClick = () => {
    setEditMode(false);
    setSelectedTestLabel(null);
    setSelectedTestValue(null);
  };

  const handleGenerateReport = async (testItem: any, index: number) => {
    try {
      setLoadingEditIndex(index);
      const res = await generateLabReport(Number(lab_id), Number(deptId), Number(testItem?.service_cat_id));

      if (res?.report_url) {
        setReportUrls((prev) => ({
          ...prev,
          [index]: res.report_url.replace(/\\/g, ''),
        }));

        notification.success({
          message: 'Lab report already generated. You can now view it.',
        });
      } else {
        notification.warning({
          message: 'Your Lab Report has been added to the Queue! Please try generating it again in a short time',
        });
      }
    } catch (err) {
      handleErrorMessages(err);
      console.error('Failed to generate report:', err);
      // notificationController.error({ message: String(err) });
    } finally {
      setLoadingEditIndex(null);
    }
  };

  const handleButtonClick = (index: number) => {
    fileInputRefs.current[index]?.click();
  };

  console.log('testItems: ', testItems);

  const isValidUrl = (url: string): boolean => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  interface ErrorObject {
    status: number;
    message: string;
    errors?: any;
    data?: any;
  }

  type HandleErrorMessages = (error: ErrorObject) => void;

  const handleFileOpen = async (
    url: string | null | undefined,
    fileName = 'file',
    handleErrorMessages: HandleErrorMessages,
    skipAccessibilityCheck = false,
  ): Promise<void> => {
    try {
      // Check if URL is valid
      if (!url || !isValidUrl(url)) {
        const error: ErrorObject = {
          status: 400,
          message: `${fileName} URL is invalid or corrupted`,
        };
        handleErrorMessages(error);
        return;
      }

      // Try to fetch the file to check if it exists and is accessible
      if (!skipAccessibilityCheck) {
        try {
          const response = await fetch(url, { method: 'HEAD' });

          if (!response.ok) {
            let errorMessage = `Unable to access ${fileName}`;

            if (response.status === 404) {
              errorMessage = `${fileName} not found. It may have been moved or deleted.`;
            } else if (response.status === 403) {
              errorMessage = `Access denied to ${fileName}. Please contact support.`;
            } else {
              errorMessage = `${fileName} unavailable (Error: ${response.status})`;
            }

            const error: ErrorObject = {
              status: response.status,
              message: errorMessage,
            };
            handleErrorMessages(error);
            return;
          }
        } catch (fetchError) {
          // If HEAD request fails, still try to open the file
          console.warn(`HEAD request failed for ${fileName}, attempting to open anyway:`, fetchError);
        }
      }

      // If file is accessible, open it
      window.open(url, '_blank');
    } catch (error: unknown) {
      console.error(`Error opening ${fileName}:`, error);

      // Handle network errors and other exceptions
      let errorMessage = `Unable to open ${fileName}`;
      let statusCode = 500;

      if (error instanceof Error) {
        if (error.name === 'TypeError' || error.message.includes('NetworkError')) {
          errorMessage = `Network error accessing ${fileName}. Please check your connection.`;
          statusCode = 0; // Network error
        } else if (error.message) {
          errorMessage = `${fileName} error: ${error.message}`;
        }
      } else {
        errorMessage = `${fileName} error: Unknown error occurred`;
      }

      const normalizedError: ErrorObject = {
        status: statusCode,
        message: errorMessage,
      };

      handleErrorMessages(normalizedError);
    }
  };

  const getCertifiedReportUrl = (item: any) => {
    // Priority: newly uploaded file URL > original certified report URL
    return uploadedFiles[item.maintest_id]?.url || item.certified_report_url;
  };

  return (
    <>
      <Card bordered style={{ borderRadius: 12, marginBottom: 24, borderColor: '#718EBF' }} bodyStyle={{ padding: 16 }}>
        <BaseRow justify="space-between" align="middle">
          <BaseCol>
            <Text strong style={{ fontSize: 16 }}>
              {divisionCode}
            </Text>
          </BaseCol>

          <BaseCol
            style={{
              flex: status !== 'PENDING_TA' ? 1 : undefined,
              textAlign: status !== 'PENDING_TA' ? 'center' : 'left',
            }}
          >
            <Text strong style={{ fontSize: 16 }}>
              {divisionName}
            </Text>
          </BaseCol>
        </BaseRow>

        <Divider style={{ margin: '12px 0 16px' }} />

        {/* Dropdown + Add Button */}
        {(status === 'PENDING_TA' || status === 'PENDING_MA' || status === 'PENDING_IA') && (
          <BaseRow align="middle" gutter={12} style={{ marginBottom: 16 }}>
            <BaseCol flex="auto">
              <Space size={8}>
                {!editMode ? (
                  <Button type="primary" size="small" loading={loadingEdit} onClick={handleEditClick}>
                    Add
                  </Button>
                ) : (
                  <>
                    <BaseSelect
                      showSearch
                      placeholder="Select Test"
                      options={testOptions}
                      size="small"
                      style={{ minWidth: 350 }}
                      value={dropdownTestValue}
                      onChange={(value, option) => {
                        setDropdownTestValue(value as string);
                        setSelectedTestValue(value as string);
                        setSelectedTestLabel((option as any)?.label || null);
                      }}
                    />
                    <Button type="default" size="small" onClick={handleAddTest} loading={loadingEdit}>
                      {getAddTestButtonText()}
                    </Button>
                    <Button type="default" icon={<CheckOutlined />} size="small" onClick={handleDoneClick} />
                  </>
                )}
              </Space>
            </BaseCol>
          </BaseRow>
        )}

        {/* Listed test rows */}
        <div>
          {currentTests.length === 0 ? (
            <Text type="secondary">No tests added yet.</Text>
          ) : (
            testItems.map((item, index) => (
              <BaseRow
                key={index}
                align="middle"
                style={{ padding: '8px 0', flexWrap: 'nowrap' }}
                justify="space-between"
              >
                <BaseCol span={4}>
                  <Text style={{ fontSize: 14 }}>{item.code}</Text>
                </BaseCol>
                <BaseCol span={4}>
                  <Space>
                    {item.icon}
                    <Text style={{ fontSize: 14 }}>{item.name}</Text>
                  </Space>
                </BaseCol>
                <BaseCol span={4}>
                  <Text style={{ fontSize: 14 }}>Admin</Text>
                </BaseCol>

                <BaseCol span={4}>
                  <Text
                    style={{
                      color:
                        statusColors[
                          lab_type === 'DIRECT_SALES'
                            ? item.status_label_ds || 'Pending'
                            : item.test_status || 'Pending'
                        ],
                      fontSize: 14,
                    }}
                  >
                    {lab_type === 'DIRECT_SALES' ? item.status_label_ds || 'Pending' : item.test_status || 'Pending'}
                  </Text>
                </BaseCol>

                <BaseCol span={8} style={{ textAlign: 'right' }}>
                  {(status === 'PENDING_TA' ||
                    status === 'PENDING_MA' ||
                    (status === 'PENDING_IA' && context === 'lab-tests')) && (
                    <Space>
                      <Button
                        size="small"
                        onClick={() => handleTestAction(item, index, 'view')}
                        loading={loadingEditIndex === index}
                      >
                        View
                      </Button>
                      <Popconfirm
                        title="Are you sure you want to remove this Item?"
                        onConfirm={() => handleRemoveTest(item, index)}
                        okText="Yes"
                        cancelText="No"
                      >
                        <Button size="small" danger>
                          Remove
                        </Button>
                      </Popconfirm>
                    </Space>
                  )}

                  {(status === 'PENDING_PAYMENT' ||
                    status === 'PENDING_ITR' ||
                    status === 'DONE' ||
                    status === 'PENDING_ITRA' ||
                    status === 'PENDING_FRS') &&
                    context === 'lab-tests' && (
                      <Button
                        size="small"
                        onClick={() => handleTestAction(item, index, 'view')}
                        loading={loadingEditIndex === index}
                      >
                        View
                      </Button>
                    )}
                  {status === 'PENDING_ITR' && context === 'test-results' && (
                    <Space>
                      <Button
                        size="small"
                        onClick={() =>
                          handleResultParameters(
                            item,
                            index,
                            item.test_status === 'Pending Test Results Approval' ? 'view' : 'add',
                          )
                        }
                        loading={loadingEditIndex === index}
                      >
                        {item.test_status === 'Pending Test Results Approval' ? 'View' : 'Submit Result'}
                      </Button>
                    </Space>
                  )}
                  {status === 'PENDING_PRODUCTION' && context === 'test-results' && (
                    <Space>
                      <Button
                        size="small"
                        onClick={() =>
                          handleResultParameters(
                            item,
                            index,
                            item.test_status === 'Pending Approval' ||
                              item.test_status === 'Collected' ||
                              item.test_status === 'Pending Collection'
                              ? 'view'
                              : 'add',
                          )
                        }
                        loading={loadingEditIndex === index}
                        style={{ borderColor: '396AFF', color: '396AFF' }}
                      >
                        {item.test_status === 'Pending Approval' ||
                        item.test_status === 'Collected' ||
                        item.test_status === 'Pending Collection'
                          ? 'View'
                          : 'Ready'}
                      </Button>
                    </Space>
                  )}
                  {status === 'PENDING_ITRA' && context === 'test-results' && (
                    <Space>
                      <Button size="small" onClick={() => handleResultParameters(item, index, 'view')}>
                        View
                      </Button>
                    </Space>
                  )}
                  {status === 'PENDING_FRS' && context === 'test-results' && (
                    <Space>
                      <Button size="small" onClick={() => handleResultParameters(item, index, 'view')}>
                        View Test
                      </Button>

                      {!item.generated_report ? (
                        <Button
                          size="small"
                          type="ghost"
                          onClick={() => handleGenerateReport(item, index)}
                          loading={loadingEditIndex === index}
                        >
                          Generate Report
                        </Button>
                      ) : (
                        <Button
                          size="small"
                          type="ghost"
                          href={item.generated_report_url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          View Generated Report
                        </Button>
                      )}

                      {/* Keep existing reportUrls functionality */}
                      {reportUrls[index] && (
                        <Button
                          size="small"
                          type="ghost"
                          href={reportUrls[index]}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          View Report
                        </Button>
                      )}
                    </Space>
                  )}
                  {status === 'PENDING_FRS' && context === 'final-results' && (
                    <>
                      <input
                        type="file"
                        ref={(el) => {
                          fileInputRefs.current[index] = el;
                        }}
                        style={{ display: 'none' }}
                        onChange={(e) => handleUpload(item, index, e)}
                        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                      />
                      <Space>
                        <Button size="small" onClick={() => handleResultParameters(item, index, 'view')}>
                          View Results
                        </Button>
                        <Button
                          onClick={() => handleButtonClick(index)}
                          loading={loadingEditIndex === index}
                          size="small"
                          type="primary"
                        >
                          {item.certified_report || uploadedFiles[item.maintest_id] ? 'Re-upload' : 'Upload'}
                        </Button>

                        {/* Show view button if there's either a certified report or newly uploaded file */}
                        {(item.certified_report || uploadedFiles[item.maintest_id]) && (
                          <Button
                            size="small"
                            onClick={() =>
                              handleFileOpen(
                                getCertifiedReportUrl(item),
                                'Certified Report',
                                handleErrorMessages,
                                !!uploadedFiles[item.maintest_id],
                              )
                            }
                          >
                            View Certified Report
                          </Button>
                        )}
                      </Space>
                    </>
                  )}
                  {status === 'PENDING_FA' && context === 'lab-tests' && (
                    <Button size="small" onClick={() => handleTestAction(item, index, 'view')}>
                      View
                    </Button>
                  )}
                  {status === 'PENDING_COLLECTION' &&
                    context === 'lab-tests' &&
                    (item.status_label_ds === 'Approved' ? (
                      <Button size="small" onClick={() => handleResultParameters(item, index, 'add')}>
                        Ready Collection
                      </Button>
                    ) : (
                      <Button size="small" onClick={() => handleResultParameters(item, index, 'view')}>
                        View
                      </Button>
                    ))}
                  {status === 'PENDING_FA' && context === 'test-results' && (
                    <Space>
                      <Button size="small" onClick={() => handleResultParameters(item, index, 'view')}>
                        View
                      </Button>
                      {item.hasGeneratedReport && (
                        <Button
                          size="small"
                          type="ghost"
                          href={item.generated_report_url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          View Generated Report
                        </Button>
                      )}
                    </Space>
                  )}
                  {status === 'PENDING_FA' && context === 'final-results' && (
                    <Space>
                      <Button size="small" onClick={() => handleResultParameters(item, index, 'view')}>
                        View
                      </Button>
                      {item.hasCertifiedReport && (
                        <Button
                          size="small"
                          type="primary"
                          onClick={() =>
                            handleFileOpen(getCertifiedReportUrl(item), 'Final Lab Report', handleErrorMessages)
                          }
                        >
                          Final Lab Report
                        </Button>
                      )}
                    </Space>
                  )}
                  {status === 'DONE' && (context === 'final-results' || context === 'test-results') && (
                    <Space>
                      <Button size="small" onClick={() => handleResultParameters(item, index, 'view')}>
                        View
                      </Button>
                      {context === 'final-results' && item.hasCertifiedReport && (
                        <Button
                          size="small"
                          type="primary"
                          onClick={() =>
                            handleFileOpen(getCertifiedReportUrl(item), 'Final Lab Report', handleErrorMessages)
                          }
                        >
                          Final Lab Report
                        </Button>
                      )}
                    </Space>
                  )}
                </BaseCol>
              </BaseRow>
            ))
          )}
        </div>
      </Card>
      {selectedTestValue && selectedTestLabel && modalVisible && (
        <TestDetailsModal
          visible={modalVisible}
          lab_id={lab_id}
          mainTest={selectedTestLabel}
          mainTestId={selectedTestValue}
          onClose={() => setModalVisible(false)}
          assigningParameters={AssigningData?.assigning_parameters}
          labServices={AssigningData?.lab_services}
          selectedLabServices={AssigningData?.selected_lab_services}
          deptId={deptId}
          technician_options={AssigningData?.technician_options}
          additionalInfo={AssigningData?.additionalInfo}
          mode={testModalMode}
          onTestUpdated={handleTestUpdated}
          status={status}
          wf_actions={AssigningData?.wf_actions || []}
          service_cat_id={AssigningData?.service_cat_id}
          lab_samples={AssigningData?.lab_samples || []}
          lab_type={lab_type}
          divisionCode={divisionCode}
        />
      )}
      {selectedResultsTestName && (
        <ResultsModal
          visible={resultsModalVisible}
          testName={selectedResultsTestName}
          resultParameters={resultsData}
          onClose={() => setResultsModalVisible(false)}
          lab_id={lab_id}
          deptId={deptId}
          mode={resultModalMode}
          status={status}
          wf_actions={wf_actions}
          service_cat_id={resultsData?.lab_service_category_id}
          onTestUpdated={handleTestUpdated}
        />
      )}
    </>
  );
};

export default DivisionalItemsCard;
